import { useEffect, useMemo, useState } from 'react';
import { DororaidIcon } from '../../../shared/ui/DororaidIcon.jsx';
import { RaidPracticeDeckDisplay, RaidPracticeDeckPicker } from '../../raid-practice/shared/RaidPracticeDeckPicker.jsx';
import { requestRaidControlAttack } from '../api/raidControlRequestApi.js';
import '../../../styles/raid-control-member-request.css';

function formatDamage(value) {
  return `${Number(value || 0).toLocaleString('ko-KR', { maximumFractionDigits: 3 })}억`;
}

export function MemberAttackRequestDialog({ room, userId, onClose, onDone }) {
  const selfMember = room.members.find((member) => member.userId === userId) || null;
  const filteredOwnPracticeRecords = room.availablePracticeRecordsByMember?.get(selfMember?.memberOpenid) || [];
  const availableOwnPracticeById = useMemo(() => new Map(filteredOwnPracticeRecords.map((record) => [record.id, record])), [filteredOwnPracticeRecords]);
  const ownPracticeRecords = useMemo(() => (room.context.practiceRecords || [])
    .filter((record) => record.memberOpenid === selfMember?.memberOpenid && !record.used)
    .filter((record) => availableOwnPracticeById.has(record.id))
    .map((record) => availableOwnPracticeById.get(record.id))
    .sort((left, right) => Number(left.bossNo) - Number(right.bossNo) || Number(left.slotNo) - Number(right.slotNo)),
  [availableOwnPracticeById, room.context.practiceRecords, selfMember?.memberOpenid]);
  const initialBossNo = ownPracticeRecords.find((record) => Number(record.bossNo) === Number(room.selectedBossNo))?.bossNo
    || ownPracticeRecords[0]?.bossNo
    || room.selectedBossNo
    || 1;
  const [mode, setMode] = useState(ownPracticeRecords.length ? 'saved' : 'custom');
  const [bossNo, setBossNo] = useState(Number(initialBossNo));
  const [practiceRecordId, setPracticeRecordId] = useState('');
  const [title, setTitle] = useState('내 공격 덱');
  const [damage, setDamage] = useState('');
  const [deck, setDeck] = useState([]);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const boss = room.bosses.find((item) => Number(item.bossNo) === Number(bossNo));
  const bossPracticeRecords = useMemo(() => ownPracticeRecords.filter((record) => Number(record.bossNo) === Number(bossNo)), [bossNo, ownPracticeRecords]);
  const selectedPractice = bossPracticeRecords.find((record) => record.id === practiceRecordId) || null;

  useEffect(() => {
    if (mode !== 'saved') return;
    setPracticeRecordId((current) => bossPracticeRecords.some((record) => record.id === current)
      ? current
      : bossPracticeRecords[0]?.id || '');
  }, [bossPracticeRecords, mode]);

  const valid = Boolean(selfMember && (mode === 'saved'
    ? selectedPractice
    : title.trim() && Number(damage) > 0 && deck.filter(Boolean).length === 5));

  const submit = async (event) => {
    event.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    setError('');
    try {
      await requestRaidControlAttack({
        seasonId: room.context.season?.id,
        bossNo,
        phaseNo: boss?.currentPhase || 1,
        practiceRecordId: mode === 'saved' ? selectedPractice?.id : '',
        title,
        expectedDamageEok: Number(damage),
        deck,
        note,
      });
      await room.refresh({ silent: true });
      onDone();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : '공격 요청을 등록하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return <div className="raid-control-dialog-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
    <form className="raid-control-order-dialog raid-control-request-dialog" onSubmit={submit} role="dialog" aria-modal="true" aria-labelledby="raid-control-request-title">
      <header><div><h2 id="raid-control-request-title">내 공격 덱 승인 요청</h2></div><button type="button" onClick={onClose} aria-label="닫기"><DororaidIcon name="close" size={20} /></button></header>
      <div className="raid-control-order-mode" role="tablist" aria-label="공격 덱 입력 방식">
        <button type="button" role="tab" aria-selected={mode === 'saved'} className={mode === 'saved' ? 'active' : ''} onClick={() => setMode('saved')}>내 모의전 슬롯</button>
        <button type="button" role="tab" aria-selected={mode === 'custom'} className={mode === 'custom' ? 'active' : ''} onClick={() => setMode('custom')}>직접 작성</button>
      </div>
      <p className="raid-control-request-guide">{mode === 'saved'
        ? '실전에 사용한 니케가 없는 내 모의전 슬롯만 승인 요청할 수 있습니다.'
        : '직접 작성한 덱은 관리자 승인 후 공격 지시로 전환됩니다.'}</p>
      <label><span>보스</span><select value={bossNo} onChange={(event) => setBossNo(Number(event.target.value))}>{room.bosses.map((item) => <option key={item.bossNo} value={item.bossNo}>{item.bossName} · {item.currentPhase}단</option>)}</select></label>
      {mode === 'saved' ? <div className="raid-control-request-practice-list" role="radiogroup" aria-label="내 모의전 슬롯 선택">
        {bossPracticeRecords.length ? bossPracticeRecords.map((record) => <button key={record.id} type="button" role="radio" aria-checked={practiceRecordId === record.id} className={practiceRecordId === record.id ? 'selected' : ''} onClick={() => setPracticeRecordId(record.id)}>
          <span className="raid-control-request-practice-summary"><small>슬롯 {record.slotNo}</small><strong>{record.title || '저장 모의전'}</strong><em>{formatDamage(record.damageEok)}</em></span>
          <RaidPracticeDeckDisplay deck={record.deck} mobile />
          {record.note ? <p>{record.note}</p> : null}
        </button>) : <div className="raid-control-request-practice-empty"><strong>이 보스에 사용할 내 모의전 슬롯이 없습니다.</strong><button type="button" onClick={() => setMode('custom')}>직접 작성으로 전환</button></div>}
      </div> : <>
        <div className="raid-control-custom-order-primary">
          <label><span>덱 이름</span><input maxLength={80} value={title} onChange={(event) => setTitle(event.target.value)} /></label>
          <label><span>예상 피해량(억)</span><input type="number" min="0.001" step="0.001" value={damage} onChange={(event) => setDamage(event.target.value)} placeholder="예: 650" /></label>
        </div>
        <div className="raid-control-custom-deck"><span>니케 선택 <b>{deck.filter(Boolean).length}/5</b></span><RaidPracticeDeckPicker value={deck} onChange={setDeck} memberOpenid={selfMember?.memberOpenid || ''} mobile /></div>
      </>}
      <label><span>요청 메모</span><textarea rows={3} maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} placeholder="예: 막타 가능, 버스트 지연 필요" /></label>
      {error ? <p className="raid-control-command-error">{error}</p> : null}
      <div><button type="button" onClick={onClose}>닫기</button><button type="submit" className="primary" disabled={!valid || saving}>{saving ? '등록 중…' : '승인 요청'}</button></div>
    </form>
  </div>;
}
